import { useEffect } from 'react';
import { Breadcrumb, useGlobalStore } from '@/stores';

const TITLE_TEMPLATE = 'Stan - %s';

/**
 * Hook to set breadcrumbs and automatically update document title
 * Matches Vue's behavior where breadcrumbs are joined to create the page title
 *
 * @param breadcrumbs - Array of breadcrumb items
 *
 * @example
 * ```tsx
 * useBreadcrumbs([{ title: 'My Appointments', to: '/appointments', active: true, id: 'appointments' }]);
 * // Sets document.title to "Stan - My Appointments"
 * ```
 *
 * @example
 * ```tsx
 * useBreadcrumbs([
 *   { title: 'My Appointments', to: '/appointments', active: false, id: 'appointments' },
 *   { title: 'Overview', to: '/appointments/details/123', active: true, id: 'details' }
 * ]);
 * // Sets document.title to "Stan - My Appointments - Overview"
 * ```
 */
export const useBreadcrumbs = (breadcrumbs: Breadcrumb[]) => {
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);

    useEffect(() => {
        setBreadcrumbs(breadcrumbs);

        // Set document title from breadcrumbs (matching Vue behavior)
        // Join all breadcrumb titles with ' - ' to match Vue's breadcrumbs() computed property
        const breadcrumbTitles = breadcrumbs.map(b => b.title).filter(Boolean);
        const pageTitle = breadcrumbTitles.join(' - ') || 'Home';

        // Store previous title to restore on unmount
        const previousTitle = document.title;

        // Set the new title using the template format
        document.title = TITLE_TEMPLATE.replace('%s', pageTitle);

        // Restore previous title on unmount
        return () => {
            document.title = previousTitle;
        };
    }, [breadcrumbs, setBreadcrumbs]);
};
